import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { MemberService, ProjectService, ToastService, ToastType } from '../providers';
import { MotesLoginService } from './motes-login.service';

@Injectable()
export class UserAuthGuard  {

    constructor(private loginService: MotesLoginService) {
    }

    public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        // Create a login request with user rights
        this.loginService.requestUserLogin();
        return this.loginService.watchUserRights().pipe(map(userRights => {
            return userRights != null;
        }));
    }
}

/**
 * Login overlay admin guard
 * @deprecated Still used in studio but to be deprecated
 */
@Injectable({ providedIn: 'root' })
export class AdminAuthGuard  {

    constructor(private loginService: MotesLoginService) {
    }

    public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        return this.requestAdminLogin();
    }

    public canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        return this.requestAdminLogin();
    }

    private requestAdminLogin(): Observable<boolean> {
        let userRights = this.loginService.userRights;

        // Request admin login is user is not already authenticated
        if(!userRights.isAdmin && !userRights.isSuperAdmin) {
            this.loginService.requestAdminLogin();
        }

        // Monitor admin rights
        return this.loginService.watchUserRights().pipe(map(userRights => {
            if(!userRights) return false;
            return userRights.isAdmin || userRights.isSuperAdmin;
        }));
    }
}


/**
 * Auth guard for creator that uses angular router redirect in case user is not logged in
 */
@Injectable({ providedIn: 'root' })
export class CreatorAuthGuard  {

    constructor(private loginService: MotesLoginService, private router: Router) {
    }

    public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        return this.requestAdminLogin(state);
    }

    public canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        return this.requestAdminLogin(state);
    }

    private requestAdminLogin(state: RouterStateSnapshot): Observable<boolean> {
        // Watch admin rights
        return this.loginService.watchUserRights().pipe(map(userRights => {
            // Navigate to login page if no admin user is logged in
            if(!userRights?.isAdmin && !userRights?.isSuperAdmin) {
                this.router.navigate(['/login'], { queryParams: { redirect: state.url } });
            }
            // Return true if user has permission to visit admin page
            return userRights?.isAdmin || userRights?.isSuperAdmin;
        }));
    }
}

/**
 * Auth guard for creator super admin pages
 */
@Injectable({ providedIn: 'root' })
export class SuperAdminAuthGuard {

    constructor(private loginService: MotesLoginService, private router: Router) {
    }

    public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        return this.requestSuperAdminLogin(state);
    }

    public canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        return this.requestSuperAdminLogin(state);
    }

    private requestSuperAdminLogin(state: RouterStateSnapshot): Observable<boolean> {
        // Watch admin rights
        return this.loginService.watchUserRights().pipe(map(userRights => {
            // Navigate to login page if no admin user is logged in
            if(!userRights?.isSuperAdmin) {
                this.router.navigate(['/login'], { queryParams: { redirect: state.url } });
            }
            // Return true if user has permission to visit admin page
            return Boolean(userRights?.isSuperAdmin);
        }));
    }
}

/**
 * Determine if user is a member of the project they are trying to access,
 * or if they should be redirected to 404
 */
@Injectable({ providedIn: 'root' })
export class ProjectAccessGuard  {

    constructor(private projectService: ProjectService,
        private loginService: MotesLoginService,
        private memberService: MemberService,
        private router: Router,
        private toastService: ToastService) {
    }

    public async canActivate(route: ActivatedRouteSnapshot): Promise<boolean> {
        return this.requestProjectAccess(route);
    }

    private async requestProjectAccess(route: ActivatedRouteSnapshot): Promise<boolean> {
        // Fetch project key from URL
        const projectKey = route.params['projectKey'];
        if(!projectKey) return true;

        // Grand access if user is a member (and project exists)
        let user = await this.loginService.getUserCurrentRights();
        let access = await this.projectService.userHasAccess(user.uid, projectKey);
        if(access || user.isSuperAdmin) return true;

        // Go to 404 if there is no "invited" query param in URL
        let pendingMemberKey = route.queryParams['invited'];
        if(!pendingMemberKey) this.router.navigate(['/404']);

        // Go to dashboard if pending member has expired (or key has been fiddled with in URL)
        let pendingMember = await this.memberService.getPendingMember(pendingMemberKey);
        if(!pendingMember) {
            this.toastService.addToast($localize`You tried to access a world that you are not a member of. Did your invitation expire? No worries! Other members can always invite you again.`, ToastType.INFO, 20000);
            this.router.navigate(['']);
        }

        // Go to 404 if project has been deleted (or key has been fiddled with in URL)
        if(pendingMember.projectKey != projectKey) this.router.navigate(['/404']);

        // Otherwise, grand access
        this.toastService.addToast($localize`You are now a member of this world!`, ToastType.OK);
        return true;
    }
}
